import React, { useState } from "react";
import { Link } from "react-router-dom";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import "../styles/Home.css";
import { FrontendIcons, BackendIcons, OtherIcons } from "../helpers/IconList";
import IconDisplay from "../components/IconDisplay";
import { IconPlayground } from "../components/IconPlayground";
import { WeatherProvider } from "../weather/WeatherContext";
import WeatherSky from "../weather/WeatherSky";
import WeatherLine from "../weather/WeatherLine";
import athenaLeaf from "../assets/icons/athenahealth_leaf.png";
import HealthSearchCard from "../assets/graphics/HealthSearch_Card.svg";
// Photos with other people in them are hidden for now; uncomment these and their entries below to bring them back.
// import onsiteTeam from "../assets/onsite/onsite-team.jpg";
// import onsiteTnTeam from "../assets/onsite/onsite-tn-midtown-team.jpg";
// import onsiteTnWhiteboard from "../assets/onsite/onsite-tn-brentwood-whiteboard.jpg";
import onsiteTnHallway from "../assets/onsite/onsite-tn-brentwood-hallway.jpg";
import OnsiteRoute from "../components/OnsiteRoute";
import PhotoLightbox from "../components/PhotoLightbox";

// The first photo is the large one beside the route; the rest sit in the row below it.
// Professional photos (Southern Pediatrics, Tennessee) are credited to athenahealth under the row.
const onsitePhotos = [
  // {
  //   src: onsiteTnWhiteboard,
  //   width: 1600,
  //   height: 1067,
  //   position: "object-[55%_50%]",
  //   caption: "Southern Pediatrics · Brentwood",
  //   alt: "Mohamed Oulal and athenahealth colleagues working through a workflow on a whiteboard at Southern Pediatrics' Brentwood office",
  // },
  // {
  //   src: onsiteTeam,
  //   width: 1600,
  //   height: 1200,
  //   position: "object-center",
  //   caption: "Sound Beach Pediatrics · Stamford, CT",
  //   alt: "Mohamed Oulal and the team at Sound Beach Pediatrics, reviewing the new clinical inbox on a wall display",
  // },
  // {
  //   src: onsiteTnTeam,
  //   width: 1000,
  //   height: 1499,
  //   position: "object-[50%_62%]",
  //   caption: "Southern Pediatrics · Midtown",
  //   alt: "Mohamed Oulal with his athenahealth teammates at Southern Pediatrics' Midtown office in Tennessee",
  // },
  {
    src: onsiteTnHallway,
    width: 1600,
    height: 1067,
    position: "object-[58%_50%]",
    caption: "Southern Pediatrics · Brentwood",
    alt: "Mohamed Oulal working on a laptop in a hallway at Southern Pediatrics' Brentwood office",
  },
];
const [leadPhoto, ...rowPhotos] = onsitePhotos;

// Opens the photo full size when clicked; the zoom badge hints that it can be.
function PhotoButton({ photo, onOpen, imgClassName, className = "" }) {
  return (
    <button
      type="button"
      onClick={onOpen}
      aria-label={`View larger: ${photo.caption}`}
      className={`group relative block w-full h-full cursor-zoom-in ${className} rounded-xl focus:outline-none focus-visible:ring-4 focus-visible:ring-[#eb4d5c] dark:focus-visible:ring-[#c9a7ff]`}
    >
      <img
        src={photo.src}
        alt={photo.alt}
        width={photo.width}
        height={photo.height}
        loading="lazy"
        className={`${imgClassName} ${photo.position} rounded-xl shadow-md transition duration-200 group-hover:brightness-[1.06] group-hover:shadow-lg`}
      />
      <span
        aria-hidden="true"
        className="absolute right-3 top-3 flex h-9 w-9 items-center justify-center rounded-full bg-black/60 text-white backdrop-blur-sm opacity-80 sm:opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100"
      >
        <ZoomInIcon fontSize="small" />
      </span>
      <span className="absolute left-3 bottom-3 rounded-full bg-black/60 px-3 py-1 text-sm font-semibold text-white backdrop-blur-sm">
        {photo.caption}
      </span>
    </button>
  );
}

// Every site visited this fall, in route order
const onsiteStops = [
  { site: "Mountain View Medical Center", city: "Forest Grove", state: "OR", kind: "hospital" },
  { site: "athenahealth office", city: "Boston", state: "MA", kind: "office" },
  // Set pictured: true again when the Sound Beach photo comes back
  { site: "Sound Beach Pediatrics", city: "Stamford", state: "CT", kind: "pediatrics" },
  { site: "Greater Lawrence Family Health Center", city: "Lawrence", state: "MA", kind: "health-center" },
  { site: "Southern Pediatrics", city: "Midtown & Brentwood", state: "TN", kind: "clinic", pictured: true },
];

function Home() {
  const [openPhoto, setOpenPhoto] = useState(null);
  const outlineButton =
    "inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-lg font-semibold border-2 border-[#eb4d5c] text-[#eb4d5c] hover:bg-[#eb4d5c] hover:text-white dark:border-[#c9a7ff] dark:text-[#c9a7ff] dark:hover:bg-[#c9a7ff] dark:hover:text-[#0f193d] transition-colors";

  return (
    <WeatherProvider>
    <div className="w-full">
      <div className="w-full flex flex-col text-[black] dark:text-[white]">
        {/* Boston's live weather plays across the full width behind the hero */}
        <div className="relative w-full pb-10 lg:pb-12">
        <WeatherSky />
        {/* Hero: stacked and centered on phones, photo beside text on larger screens */}
        <section className="relative w-full max-w-5xl mx-auto px-4 pt-8 lg:pt-14 flex flex-col lg:flex-row items-center gap-5 lg:gap-12">
          <img
            src="/mohamed-oulal.jpg"
            alt="Mohamed Oulal"
            width="224"
            height="224"
            className="w-36 h-36 lg:w-56 lg:h-56 shrink-0 rounded-full object-cover border-4 border-[#eb4d5c] dark:border-[#a1a5b4] shadow-lg"
          />
          <div className="flex min-w-0 max-w-full flex-col items-center lg:items-start text-center lg:text-left">
            {/* Above-the-fold pointer to the featured side project; the full banner sits below the on-site section */}
            <Link
              to="/healthsearch"
              className="group mb-3 inline-flex max-w-full items-center gap-2 rounded-full bg-[#0f193d] py-1.5 pl-3 pr-2 text-[15px] font-semibold text-white shadow-md ring-1 ring-[#f07364]/50 transition hover:ring-2 hover:ring-[#f07364] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#f07364] dark:ring-[#c9a7ff]/60 dark:hover:ring-[#c9a7ff]"
            >
              <span aria-hidden="true" className="relative flex h-2.5 w-2.5 shrink-0">
                <span className="live-ping absolute inline-flex h-full w-full rounded-full bg-[#5ef0a8] opacity-75" />
                <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-[#38e08f]" />
              </span>
              <span className="truncate">
                <span className="text-[#ffb3a9] dark:text-[#d8bfff]">Live now:</span> HealthSearch<span className="hidden sm:inline">, my health price search</span>
              </span>
              <span aria-hidden="true" className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#f07364] transition-transform group-hover:translate-x-0.5 dark:bg-[#c9a7ff] dark:text-[#0f193d]">
                <ArrowForwardIcon style={{ fontSize: 16 }} />
              </span>
            </Link>
            <h1 className="text-[42px] sm:text-5xl lg:text-[56px] leading-tight text-[#eb4d5c] font-bold dark:text-[#a1a5b4] pb-1">Hi, I'm <span className="whitespace-nowrap">Mohamed Oulal</span></h1>
            <p className="text-2xl lg:text-3xl font-semibold pb-1">
              Senior Software Engineer at{" "}
              <a href="https://www.athenahealth.com" target="_blank" rel="noreferrer" className="text-[#6b2fa0] dark:text-[#c9a7ff] hover:underline whitespace-nowrap"><img src={athenaLeaf} alt="" aria-hidden="true" className="inline-block h-[0.95em] w-auto mr-1 align-[-0.1em]" />athenahealth</a>
            </p>
            <WeatherLine />
            <p className="text-xl lg:text-[22px] leading-relaxed text-[#333] dark:text-[#d6d9e3]">
              5+ years building production healthcare software, from React front ends and Java Spring Boot
              services to AI features inside an EHR.
            </p>
            <div className="flex flex-wrap items-center justify-center lg:justify-start gap-3 pt-6">
              <Link
                to="/experience"
                className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-lg font-semibold bg-[#eb4d5c] text-white hover:bg-[#d63f4e] dark:bg-[#c9a7ff] dark:text-[#0f193d] dark:hover:bg-[#b48ff5] transition-colors"
              >
                See my experience
                <ArrowForwardIcon fontSize="small" aria-hidden="true" />
              </Link>
              <a href="/Mohamed_Oulal_Resume.pdf" target="_blank" rel="noreferrer" className={outlineButton}>
                <DescriptionOutlinedIcon fontSize="small" aria-hidden="true" />
                Resume
              </a>
              <a href="https://www.linkedin.com/in/mohamedoulal/" target="_blank" rel="noreferrer" className={outlineButton}>
                <LinkedInIcon fontSize="small" aria-hidden="true" />
                LinkedIn
              </a>
              <Link to="/contact" className={outlineButton}>
                <MailOutlineIcon fontSize="small" aria-hidden="true" />
                Contact
              </Link>
            </div>
          </div>
        </section>
        </div>

        <section className="w-full max-w-5xl mx-auto px-4 pt-4 flex flex-col items-center lg:items-start">
          <h2 className="text-4xl lg:text-5xl font-bold pb-2 text-center lg:text-left">On-site with providers</h2>
          <p className="pb-3 text-sm font-semibold tracking-widest uppercase text-[#eb4d5c] dark:text-[#c9a7ff]">Sept – Oct 2026</p>
          <p className="text-lg lg:text-xl text-center lg:text-left pb-7">
            This fall, I visited 5 provider organizations in 5 weeks, sitting with physicians and staff as they used our new
            AI-powered clinical inbox and shipping a new release at every visit, often turning their feedback into
            working features within about an hour.{" "}
            <a
              href="https://www.linkedin.com/posts/athenahealth_what-a-special-24-hours-with-four-dynamo-activity-7510790487952400385-MB2q"
              target="_blank"
              rel="noreferrer"
              className="text-[#6b2fa0] dark:text-[#c9a7ff] underline"
            >
              See athenahealth&apos;s post about it
            </a>
          </p>
          <div className="w-full grid grid-cols-1 md:grid-cols-2 gap-6 items-stretch">
            {/* On wider screens the photo fills the route's height instead of setting it */}
            <figure className="relative md:min-h-[18rem]">
              <PhotoButton
                photo={leadPhoto}
                onOpen={() => setOpenPhoto(0)}
                className="md:absolute md:inset-0"
                imgClassName="w-full h-auto max-h-[28rem] md:max-h-none md:h-full object-cover"
              />
            </figure>
            <OnsiteRoute stops={onsiteStops} />
          </div>
          {rowPhotos.length > 0 && (
          <div className="w-full grid grid-cols-1 sm:grid-cols-3 gap-4 pt-6">
            {rowPhotos.map((photo, i) => (
              <figure key={photo.src} className="relative">
                <PhotoButton
                  photo={photo}
                  onOpen={() => setOpenPhoto(i + 1)}
                  imgClassName="w-full h-auto max-h-[26rem] sm:max-h-none sm:h-72 object-cover"
                />
              </figure>
            ))}
          </div>
          )}
          <PhotoLightbox photos={onsitePhotos} index={openPhoto} onClose={() => setOpenPhoto(null)} onChange={setOpenPhoto} />
          <p className="w-full pt-2 text-right text-xs text-[#555] dark:text-[#c9cfe3]">Photos: athenahealth</p>
        </section>

        {/* Featured side project: one compact banner, kept below the on-site story so it doesn't compete with it */}
        <section aria-labelledby="featured-project" className="w-full max-w-5xl mx-auto px-4 pt-12 lg:pt-14">
          <div className="relative overflow-hidden rounded-3xl bg-[#0f193d] text-white shadow-lg ring-1 ring-[#f07364]/40 dark:ring-[#c9a7ff]/50">
            <div aria-hidden="true" className="pointer-events-none absolute -right-16 -top-20 h-64 w-64 rounded-full bg-[#f07364] opacity-20 blur-2xl dark:bg-[#c9a7ff]" />
            <div className="relative flex flex-col md:flex-row items-center gap-5 md:gap-8 p-6 sm:p-8">
              <img
                src={HealthSearchCard}
                alt=""
                aria-hidden="true"
                width="500"
                height="360"
                className="w-28 h-28 md:w-36 md:h-36 shrink-0 rounded-2xl object-cover ring-1 ring-white/20"
              />
              <div className="flex flex-1 flex-col items-center md:items-start text-center md:text-left">
                <p className="flex items-center gap-2 pb-1 text-sm font-semibold tracking-widest uppercase text-[#ffb3a9] dark:text-[#d8bfff]">
                  <span aria-hidden="true" className="relative flex h-2.5 w-2.5">
                    <span className="live-ping absolute inline-flex h-full w-full rounded-full bg-[#5ef0a8] opacity-75" />
                    <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-[#38e08f]" />
                  </span>
                  Side project · Live
                </p>
                <h2 id="featured-project" className="text-3xl lg:text-4xl font-bold pb-2">HealthSearch</h2>
                <p className="text-lg leading-relaxed text-[#d6d9e3]">
                  Ask a health question in plain words and get answers from MedlinePlus, nearby hospitals with their
                  ratings, and what each one actually charges, read from 49 Massachusetts hospitals&apos; own price files.
                </p>
                <ul className="flex flex-wrap justify-center md:justify-start gap-2 pt-4 text-sm font-semibold">
                  <li className="rounded-full bg-white/10 px-3 py-1">400k+ published prices</li>
                  <li className="rounded-full bg-white/10 px-3 py-1">Hybrid keyword + semantic search</li>
                  <li className="rounded-full bg-white/10 px-3 py-1">Self-hosted on a Raspberry Pi</li>
                </ul>
              </div>
              <div className="flex shrink-0 flex-row md:flex-col items-center gap-3">
                <Link
                  to="/healthsearch"
                  className="inline-flex items-center gap-2 whitespace-nowrap rounded-full bg-[#f07364] px-5 py-2.5 text-lg font-semibold text-white transition-colors hover:bg-[#e2584a] focus:outline-none focus-visible:ring-2 focus-visible:ring-white dark:bg-[#c9a7ff] dark:text-[#0f193d] dark:hover:bg-[#d8bfff]"
                >
                  Try it
                  <ArrowForwardIcon fontSize="small" aria-hidden="true" />
                </Link>
                <Link
                  to="/projects/HealthSearch"
                  className="whitespace-nowrap text-base font-semibold text-[#d6d9e3] underline underline-offset-4 hover:text-white"
                >
                  See the walkthrough
                </Link>
              </div>
            </div>
          </div>
        </section>

        <section className="w-full max-w-5xl mx-auto px-4 pt-14 lg:pt-16 pb-12 flex flex-col items-center lg:items-start">
          <h2 className="text-4xl lg:text-5xl pb-5 font-bold text-center lg:text-left">Skills</h2>
          <ul className="flex flex-wrap justify-center lg:justify-start gap-2">
            {["React", "TypeScript", "Java Spring Boot", "OpenSearch", "AI / LLMs", "Healthcare (EHR)"].map((tag) => (
              <li
                key={tag}
                className="rounded-full border border-[#eb4d5c] dark:border-[#c9a7ff] px-4 py-1 text-lg font-medium text-[#eb4d5c] dark:text-[#c9a7ff]"
              >
                {tag}
              </li>
            ))}
          </ul>
          <IconPlayground>
          <ol className="list-none w-full pt-10">
          {[
            { title: "Frontend", group: "frontend", icons: FrontendIcons },
            { title: "Backend & Data", group: "backend", icons: BackendIcons },
            { title: "Also worked with", group: "other", icons: OtherIcons },
          ].map((g) => (
            <li key={g.group} className="pb-8 flex flex-col lg:flex-row items-center lg:items-start gap-3 lg:gap-8">
              <h3 className="text-2xl font-semibold lg:w-52 lg:shrink-0 lg:pt-3 text-center lg:text-left">{g.title}</h3>
              <IconDisplay group={g.group} icons={g.icons} />
            </li>
          ))}
          </ol>
          </IconPlayground>
        </section>
      </div>
    </div>
    </WeatherProvider>
  );
}

export default Home;
